import {
  Color,
  DirectionalLight,
  FloatType,
  FogExp2,
  HemisphereLight,
  LinearSRGBColorSpace,
  MathUtils,
  PMREMGenerator,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderTarget,
  type IUniform,
  type WebGLRenderer,
} from 'three';
import { Sky } from 'three/addons/objects/Sky.js';

/**
 * Ciel de Preetham (addon Sky), soleil directionnel, lumiere d'ambiance et
 * env map derivee du ciel. cf. doc/03-ciel-et-lumiere.md
 */

/**
 * Facteur applique a la sortie du shader de ciel.
 *
 * Le modele de Preetham rend des luminances physiques : l'horizon depasse 5
 * en lineaire contre ~0,5 pour un sol ensoleille. Expose pour le sol, le ciel
 * sature en blanc ; expose pour le ciel, le sol est noir. Plutot que de
 * baisser l'exposition globale -- ce qui assombrirait aussi tout ce qui est
 * peint a la main (obus traceurs, cibles, flammes) -- on ne ramene que le
 * ciel dans la plage utile. cf. doc/03-ciel-et-lumiere.md
 */
const SKY_INTENSITY = 0.36;

/**
 * Soleil assez haut et place a l'arriere gauche de la ligne de tir : les
 * cibles sont eclairees de face, et le char ne tire pas dans le soleil.
 */
const SUN_ELEVATION_DEG = 42;
const SUN_AZIMUTH_DEG = 212;

/**
 * Demi-largeur du frustum d'ombre, en metres. Il suit le char.
 *
 * 46 couvre le char, sa poussiere et les cibles proches avec une carte de
 * 2048 : environ 4,5 cm par texel. Au-dela, l'ombre des chenilles devient un
 * escalier.
 */
const SHADOW_EXTENT = 46;

export class SkyDome {
  readonly sky: Sky;
  readonly sunDirection = new Vector3();
  readonly sunLight: DirectionalLight;

  constructor(scene: Scene, renderer: WebGLRenderer) {
    this.sky = new Sky();
    this.sky.scale.setScalar(4000);

    const u = this.sky.material.uniforms;
    // La turbidite decide de la blancheur de l'horizon, et l'horizon decide
    // de tout : au-dela de 2,5, le halo laiteux monte si haut qu'il avale les
    // cibles lointaines, qui sont justement ce qu'on cherche a voir. On reste
    // donc sur une atmosphere claire, et c'est la brume (plus bas) qui donne
    // la profondeur -- elle, on la controle finement.
    set(u, 'turbidity', 2.2);
    set(u, 'rayleigh', 2.8);
    set(u, 'mieCoefficient', 0.007);
    set(u, 'mieDirectionalG', 0.8);
    // Uniforms de nuages : presents dans les versions recentes de l'addon
    // seulement. `set` ignore silencieusement ce qui n'existe pas, pour que le
    // jeu ne casse pas si on change de version de three.
    set(u, 'cloudCoverage', 0.14);
    set(u, 'cloudDensity', 0.22);
    set(u, 'cloudSpeed', 0.00005);
    set(u, 'cloudElevation', 0.55);

    SkyDome.scaleSkyOutput(this.sky, SKY_INTENSITY);

    const phi = MathUtils.degToRad(90 - SUN_ELEVATION_DEG);
    const theta = MathUtils.degToRad(SUN_AZIMUTH_DEG);
    this.sunDirection.setFromSphericalCoords(1, phi, theta);
    set(u, 'sunPosition', this.sunDirection);

    scene.add(this.sky);

    // Brume exponentielle : elle masque le far plane, mais surtout elle donne
    // l'echelle du champ de tir. Sans elle, une cible a 250 m se lit comme
    // une cible a 60 m un peu plus petite, et la hausse devient indevinable.
    //
    // Sa couleur est *mesuree* dans le ciel plutot que choisie a la main : le
    // shader sort des luminances physiques, une couleur sRGB ordinaire
    // donnerait un horizon nettement plus sombre que le ciel.
    //
    // La densite est le reglage le plus sensible du jeu, et le critere n'est
    // pas esthetique : il faut pouvoir *viser* a 250 m. A 0,0019 une cible a
    // cette distance est deja blanchie a 30 % et devient indevinable ; a
    // 0,00075 elle ne l'est qu'a 3,5 %, tandis que les collines qui ferment
    // le champ, a 450 m, le sont a 11 % -- assez pour qu'elles reculent, pas
    // assez pour qu'elles avalent une cible qui s'y detache.
    scene.fog = new FogExp2(0x000000, 0.00075);
    scene.fog.color.copy(SkyDome.sampleHorizon(renderer, this.sky, this.sunDirection));
    // sampleHorizon detache temporairement le ciel pour le rendre seul.
    scene.add(this.sky);

    // Les intensites sont montees plutot que l'exposition : le ciel est deja
    // cadre par SKY_INTENSITY, le remonter le ferait resaturer en blanc.
    this.sunLight = new DirectionalLight(0xfff0d8, 4.0);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(2048, 2048);
    const cam = this.sunLight.shadow.camera;
    cam.left = -SHADOW_EXTENT;
    cam.right = SHADOW_EXTENT;
    cam.top = SHADOW_EXTENT;
    cam.bottom = -SHADOW_EXTENT;
    cam.near = 1;
    cam.far = 460;
    // normalBias plutot que bias seul : supprime l'acne sur le sol incline
    // sans decoller l'ombre du char de ses chenilles.
    this.sunLight.shadow.bias = -0.0004;
    this.sunLight.shadow.normalBias = 0.05;
    scene.add(this.sunLight, this.sunLight.target);

    // Sans ce rebond, les flancs du char a l'ombre tombent au noir.
    scene.add(new HemisphereLight(0xa9c6e8, 0x6b6450, 1.35));

    // L'env map derivee du ciel donne gratuitement des reflets corrects sur
    // le metal du canon et les futs du decor.
    const pmrem = new PMREMGenerator(renderer);
    const envScene = new Scene();
    envScene.add(this.sky);
    scene.environment = pmrem.fromScene(envScene).texture;
    // Compense SKY_INTENSITY : l'env map est derivee du meme ciel, assombri.
    scene.environmentIntensity = 0.35 / SKY_INTENSITY;
    scene.add(this.sky);
    pmrem.dispose();
  }

  /**
   * Multiplie la sortie du shader de ciel. On reecrit la chaine du fragment
   * shader avant la premiere compilation plutot que de passer par
   * onBeforeCompile : Sky est un ShaderMaterial ordinaire, ses uniforms sont
   * lus depuis `material.uniforms`.
   */
  private static scaleSkyOutput(sky: Sky, intensity: number): void {
    const mat = sky.material;
    const NEEDLE = 'gl_FragColor = vec4( texColor, 1.0 );';
    if (!mat.fragmentShader.includes(NEEDLE)) {
      // Le shader de l'addon a change : mieux vaut le savoir tout de suite
      // qu'avoir un ciel blanc sans explication.
      throw new Error("Sky : impossible d'injecter skyIntensity, le shader a change.");
    }
    mat.uniforms.skyIntensity = { value: intensity };
    mat.fragmentShader =
      'uniform float skyIntensity;\n' +
      mat.fragmentShader.replace(NEEDLE, 'gl_FragColor = vec4( texColor * skyIntensity, 1.0 );');
  }

  /** Radiance mesuree au zenith, pour le reglage (voir doc/03). */
  static zenith = new Color();

  /**
   * Rend le ciel dans une cible flottante et lit la radiance moyenne juste
   * au-dessus de l'horizon, dans quatre directions.
   *
   * Une cible 8 bits ne suffirait pas : le ciel depasse 1.0 et serait ecrete.
   * three.js desactive le tone mapping hors du canvas, donc ce qu'on lit est
   * bien la radiance lineaire. cf. doc/03-ciel-et-lumiere.md
   */
  private static sampleHorizon(renderer: WebGLRenderer, sky: Sky, sun: Vector3): Color {
    const fallback = new Color().setRGB(0.5, 0.62, 0.86, LinearSRGBColorSpace);
    const previous = renderer.getRenderTarget();
    let rt: WebGLRenderTarget | null = null;
    try {
      const SIZE = 16;
      rt = new WebGLRenderTarget(SIZE, SIZE, { type: FloatType });
      const cam = new PerspectiveCamera(50, 1, 0.1, 10000);
      const probeScene = new Scene();
      probeScene.add(sky);

      const buf = new Float32Array(SIZE * SIZE * 4);
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      // Quatre azimuts, legerement au-dessus de l'horizon : c'est la que se
      // detachent les collines lointaines.
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.atan2(sun.x, sun.z);
        cam.position.set(0, 0, 0);
        cam.lookAt(Math.sin(a), 0.07, Math.cos(a));
        renderer.setRenderTarget(rt);
        renderer.render(probeScene, cam);
        renderer.readRenderTargetPixels(rt, 0, 0, SIZE, SIZE, buf);
        for (let p = 0; p < buf.length; p += 4) {
          r += buf[p]!;
          g += buf[p + 1]!;
          b += buf[p + 2]!;
          n++;
        }
      }
      // Au passage : la meme mesure au zenith, utile pour juger du contraste
      // entre le haut du ciel (bleu) et l'horizon (laiteux).
      cam.lookAt(0, 1, 0);
      renderer.setRenderTarget(rt);
      renderer.render(probeScene, cam);
      renderer.readRenderTargetPixels(rt, 0, 0, SIZE, SIZE, buf);
      let zr = 0;
      let zg = 0;
      let zb = 0;
      for (let p = 0; p < buf.length; p += 4) {
        zr += buf[p]!;
        zg += buf[p + 1]!;
        zb += buf[p + 2]!;
      }
      const zn = buf.length / 4;
      SkyDome.zenith.setRGB(zr / zn, zg / zn, zb / zn, LinearSRGBColorSpace);

      renderer.setRenderTarget(previous);
      if (!n || !Number.isFinite(r + g + b)) return fallback;
      return new Color().setRGB(r / n, g / n, b / n, LinearSRGBColorSpace);
    } catch {
      // Pas de cible flottante disponible : on retombe sur un bleu correct.
      renderer.setRenderTarget(previous);
      return fallback;
    } finally {
      rt?.dispose();
    }
  }

  /** Recentre le frustum d'ombre sur le char et fait deriver les nuages. */
  update(dt: number, focus: Vector3): void {
    const time = this.sky.material.uniforms.time;
    if (time) time.value += dt;
    this.sunLight.target.position.copy(focus);
    this.sunLight.position.copy(focus).addScaledVector(this.sunDirection, 220);
  }
}

/**
 * Ecrit un uniform s'il existe.
 *
 * Les uniforms de nuages ont ete ajoutes a l'addon Sky recemment. Y ecrire en
 * dur ferait planter le jeu sur une version plus ancienne de three, pour un
 * reglage purement decoratif.
 */
function set(
  uniforms: Record<string, IUniform | undefined>,
  name: string,
  value: number | Vector3,
): void {
  const slot = uniforms[name];
  if (!slot) return;
  if (typeof value === 'number') slot.value = value;
  else if (slot.value instanceof Vector3) slot.value.copy(value);
  else slot.value = value.clone();
}
